import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  CheckCircle,
  Cancel,
  Pending,
} from '@mui/icons-material';
import partnerService from '../../services/partnerService';
import utilityService from '../../services/utilityService';
import managerService from '../../services/managerService';
import PartnerProfile from './PartnerProfile';

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPartner, setCurrentPartner] = useState(null);

  // Partner profile drawer
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Cities
  const [cities, setCities] = useState([]);

  // Categories filter
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    email: '',
    cityId: '',
    pincode: '',
    fullAddress: '',
  });

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      loadPartners();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadPartners();
  }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // When category filter changes, reset to page 0 and reload
  useEffect(() => {
    setPage(0);
    loadPartners();
  }, [selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCities();
    loadCategories();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);

      const filters = {
        order: 'createdAt DESC',
      };

      // Build where clause
      const whereConditions = {};

      if (searchText) {
        whereConditions.or = [
          { name: { regexp: `/${searchText}/i` } },
          { mobileNumber: { regexp: `/${searchText}/i` } },
          { email: { regexp: `/${searchText}/i` } },
        ];
      }

      // When filtering by category, fetch all matching partners (no pagination on server)
      // because category is on the related services, not on the partner model itself
      if (selectedCategory) {
        filters.where = Object.keys(whereConditions).length ? whereConditions : undefined;
        // Fetch all partners so we can filter by category client-side
        const allData = await partnerService.getPartners({ ...filters });
        const selectedCat = categories.find(c => c.categoryId === selectedCategory);
        const catName = selectedCat?.name || '';

        const filtered = allData.filter(p =>
          (p.services || []).some(s =>
            String(s.categoryId) === String(selectedCategory) ||
            (catName && s.categoryName === catName)
          )
        );

        setPartners(filtered);
        setTotalCount(filtered.length);
      } else {
        // Normal server-side pagination when no category filter
        filters.limit = rowsPerPage;
        filters.skip = page * rowsPerPage;
        if (Object.keys(whereConditions).length) {
          filters.where = whereConditions;
        }

        const data = await partnerService.getPartners(filters);
        setPartners(data);

        const countFilters = searchText ? whereConditions : {};
        const count = await partnerService.getPartnersCount(countFilters);
        setTotalCount(count);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
      setError('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const data = await utilityService.getCities();
      setCities(data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await managerService.getCategories();
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setEditMode(true);
      setCurrentPartner(partner);
      setFormData({
        firstName: partner.name?.split(' ')[0] || '',
        lastName: partner.name?.split(' ').slice(1).join(' ') || '',
        mobileNumber: partner.mobileNumber || '',
        email: partner.email || '',
        cityId: partner.cityId || '',
        pincode: partner.pincode || '',
        fullAddress: partner.fullAddress || '',
      });
    } else {
      setEditMode(false);
      setCurrentPartner(null);
      setFormData({
        firstName: '',
        lastName: '',
        mobileNumber: '',
        email: '',
        cityId: '',
        pincode: '',
        fullAddress: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!formData.firstName || !formData.mobileNumber || !formData.cityId) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate mobile number
    if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      const payload = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        mobileNumber: formData.mobileNumber,
        email: formData.email,
        cityId: formData.cityId,
        city: cities.find(c => c.cityId === formData.cityId)?.name || '',
        pincode: formData.pincode,
        fullAddress: formData.fullAddress,
      };

      if (editMode) {
        await partnerService.updatePartner(currentPartner.userId, payload);
        setSuccess('Partner updated successfully');
      } else {
        await partnerService.createPartner(payload);
        setSuccess('Partner created successfully');
      }

      handleCloseDialog();
      loadPartners();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving partner:', error);
      setError(error.response?.data?.message || 'Failed to save partner');
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) {
      return;
    }

    try {
      await partnerService.deletePartner(partnerId);
      setSuccess('Partner deleted successfully');
      loadPartners();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting partner:', error);
      setError('Failed to delete partner');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      verified: { label: 'Verified', color: 'success', icon: <CheckCircle /> },
      unverified: { label: 'Unverified', color: 'warning', icon: <Pending /> },
      rejected: { label: 'Rejected', color: 'error', icon: <Cancel /> },
    };

    const config = statusConfig[status] || statusConfig.unverified;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  // When category is active, partners already contains the filtered set (fetched all at once).
  // Apply client-side pagination on top of it.
  const displayedPartners = selectedCategory
    ? partners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : partners;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Partners
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Partner
        </Button>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              placeholder="Search by name, mobile number, or email"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Filter by Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.categoryId} value={cat.categoryId}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Pincode</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : displayedPartners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    {searchText || selectedCategory ? 'No partners found matching your filters' : 'No partners found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedPartners.map((partner) => (
                <TableRow key={partner.userId} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 'bold',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => setSelectedPartner(partner)}
                    >
                      {partner.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{partner.mobileNumber}</TableCell>
                  <TableCell>{partner.email || '-'}</TableCell>
                  <TableCell>{partner.city}</TableCell>
                  <TableCell>{partner.pincode}</TableCell>
                  <TableCell>{getStatusChip(partner.status)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(partner)}
                      title="Edit"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(partner.userId)}
                      title="Delete"
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Partner' : 'Add New Partner'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                name="firstName"
                value={formData.firstName}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mobile Number *"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleFormChange}
                placeholder="10-digit mobile number"
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="City *"
                name="cityId"
                value={formData.cityId}
                onChange={handleFormChange}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select City</option>
                {cities.map((city) => (
                  <option key={city.cityId} value={city.cityId}>
                    {city.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleFormChange}
                placeholder="6-digit pincode"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Address"
                name="fullAddress"
                value={formData.fullAddress}
                onChange={handleFormChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Partner Profile Drawer */}
      <PartnerProfile
        partner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
        onStatusChange={(newStatus) => {
          // Refresh the list so the status chip updates
          loadPartners();
        }}
      />
    </Box>
  );
};

export default Partners;
